import { useState } from "react";

import {
  Button,
  type Column,
  EmptyState,
  Icon,
  PeriodPicker,
  Pill,
  SearchInput,
  Select,
  Table,
  Tabs,
  type TabItem,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import { useAutospedFiles, useReexecutar } from "@/hooks/useAutosped";
import { autospedService } from "@/services/autospedService";
import { extractErrorMessage } from "@/services/axiosConfig";
import { useDebounce } from "@/hooks/useDebounce";
import { type EmpresaRow, SISTEMA_OPTIONS, type SpedFile } from "@/types/autosped";
import { getInitials } from "@/utils/initials";

import { Pagination } from "./AutospedList";
import { fileStatusPill, formatBytes, formatDateTime, origemLabel, statusAggPill } from "./helpers";
import "@/styles/operacao.css";

type FileTab = "todos" | "baixado" | "pendente" | "erro";
const PAGE_SIZE = 25;

const FILE_TABS: { id: FileTab; label: string; status?: string }[] = [
  { id: "todos", label: "Todos" },
  { id: "baixado", label: "Concluídos", status: "baixado" },
  { id: "pendente", label: "Pendentes", status: "pendente" },
  { id: "erro", label: "Com erro", status: "erro" },
];

export function AutospedDetail({
  workspaceId,
  empresa,
  onBack,
}: {
  workspaceId: number;
  empresa: EmpresaRow;
  onBack: () => void;
}) {
  const [tab, setTab] = useState<FileTab>("todos");
  const [q, setQ] = useState("");
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");
  const [sistema, setSistema] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const statusFilter = FILE_TABS.find((t) => t.id === tab)?.status;
  // Busca debounced: q cru no input, debounced na queryKey (1 request por pausa
  // de digitação, não por tecla).
  const debouncedQ = useDebounce(q.trim(), 300);
  const { data, isLoading } = useAutospedFiles(workspaceId, empresa.client_id, {
    q: debouncedQ || undefined,
    data_de: dataDe || undefined,
    data_ate: dataAte || undefined,
    sistema: sistema ?? undefined,
    status: statusFilter,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const files = data?.data ?? [];
  const total = data?.meta?.total ?? files.length;
  const totalPages = data?.meta?.total_pages ?? 1;

  const reexec = useReexecutar(workspaceId);
  const resetPage = () => setPage(0);
  const sp = statusAggPill(empresa.status_agg);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const visibleKeys = files.map((f) => String(f.id));
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) visibleKeys.forEach((k) => next.delete(k));
      else visibleKeys.forEach((k) => next.add(k));
      return next;
    });
  const reprocessarSelecionados = () => {
    const dispatchIds = new Set(
      files.filter((f) => selected.has(String(f.id))).map((f) => f.dispatch_id),
    );
    dispatchIds.forEach((dispatchId) => reexec.mutate({ dispatchId }));
    setSelected(new Set());
  };

  const baixavel = (f: SpedFile) => f.status === "baixado" || f.status === "enviado_importacao";
  const baixar = async (fileId: number) => {
    try {
      const url = await autospedService.downloadUrl(workspaceId, fileId);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      window.alert(extractErrorMessage(e));
    }
  };
  const baixarSelecionados = async () => {
    const ids = files.filter((f) => selected.has(String(f.id)) && baixavel(f)).map((f) => f.id);
    if (ids.length === 0) return;
    try {
      const blob = await autospedService.downloadZip(workspaceId, ids);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "speds.zip";
      a.click();
      URL.revokeObjectURL(url);
      setSelected(new Set());
    } catch (e) {
      window.alert(extractErrorMessage(e));
    }
  };

  const columns: Column<SpedFile>[] = [
    {
      id: "arquivo",
      header: "Arquivo",
      cell: (f) => (
        <div className="emp-table-name">
          <span className="op-file-mark">
            <Icon name="file-text" size={14} />
          </span>
          <div>
            <div className="razao op-mono">{f.nome_arquivo ?? `ARQ-${f.id}`}</div>
            {f.error_message && <div className="op-file-error">{f.error_message}</div>}
          </div>
        </div>
      ),
    },
    { id: "tipo", header: "Tipo", cell: (f) => <span className="tag-chip">{f.tipo_sped}</span> },
    {
      id: "competencia",
      header: "Competência",
      cell: (f) => <span className="op-mono">{f.competencia ?? "—"}</span>,
    },
    {
      id: "tamanho",
      header: "Tamanho",
      cell: (f) => <span className="op-mono">{formatBytes(f.tamanho_bytes)}</span>,
    },
    {
      id: "disparado",
      header: "Disparado por",
      cell: () => (
        <span className="op-origem">
          <Icon name="zap" size={13} /> {empresa.origem === "ecac" ? "Sistema" : "Equipe"}
        </span>
      ),
    },
    {
      id: "entrada",
      header: "Data de entrada",
      cell: (f) => <span className="op-mono">{formatDateTime(f.received_at)}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (f) => {
        const fp = fileStatusPill(f.status);
        return <Pill tone={fp.tone}>{fp.label}</Pill>;
      },
    },
  ];

  const tabs: TabItem<FileTab>[] = FILE_TABS.map((t) => ({ id: t.id, label: t.label }));

  return (
    <div>
      <button type="button" className="detail-back" onClick={onBack}>
        <Icon name="arrow-left" size={15} /> Voltar a Autosped
      </button>

      <div className="op-detail-hero">
        <span className="org-mono-mark op-hero-mark">{getInitials(empresa.legal_name)}</span>
        <div className="op-hero-main">
          <div className="op-hero-eyebrow">EXECUÇÃO</div>
          <h2 className="op-hero-title">{empresa.legal_name}</h2>
          <div className="op-hero-sub op-mono">{empresa.document_masked}</div>
        </div>
        <div className="op-hero-right">
          <Pill tone={sp.tone}>{sp.label}</Pill>
          <div className="op-hero-origem">
            <Icon name={empresa.origem === "ecac" ? "zap" : "user"} size={12} />{" "}
            {origemLabel(empresa.origem)}
          </div>
        </div>
      </div>

      {Object.keys(empresa.tipo_breakdown).length > 0 && (
        <div className="op-tipos">
          <span className="op-tipos-label">POR TIPO DE SPED</span>
          {Object.entries(empresa.tipo_breakdown).map(([label, n]) => (
            <span key={label} className="tag-chip">
              {label} <span className="op-tipo-count">{n}</span>
            </span>
          ))}
        </div>
      )}

      <Tabs
        items={tabs}
        active={tab}
        onChange={(t) => {
          setTab(t);
          resetPage();
        }}
      />

      <div className="op-filters">
        <SearchInput
          value={q}
          onChange={(v) => {
            setQ(v);
            resetPage();
          }}
          placeholder="Buscar por arquivo ou recibo…"
        />
        <PeriodPicker
          label="Competência"
          value={dataDe || dataAte ? { de: dataDe || null, ate: dataAte || null } : null}
          onChange={(p) => {
            setDataDe(p?.de ?? "");
            setDataAte(p?.ate ?? "");
            resetPage();
          }}
        />
        <Select
          variant="chip"
          label="Tipo SPED"
          value={sistema}
          onChange={(v) => {
            setSistema(v);
            resetPage();
          }}
          options={SISTEMA_OPTIONS}
          clearable
        />
        {(tab !== "todos" || q || dataDe || dataAte || sistema) && (
          <Button
            variant="ghost"
            size="sm"
            iconLeft="x"
            onClick={() => {
              setTab("todos");
              setQ("");
              setDataDe("");
              setDataAte("");
              setSistema(null);
              resetPage();
            }}
          >
            Limpar
          </Button>
        )}
        <span className="op-counter">
          {total} {total === 1 ? "arquivo" : "arquivos"}
        </span>
      </div>

      {selected.size > 0 && (
        <div className="op-bulkbar">
          <span className="op-bulkbar-count">
            {selected.size} {selected.size === 1 ? "arquivo selecionado" : "arquivos selecionados"}
          </span>
          <div className="op-bulkbar-actions">
            <Guard permission="autosped.download">
              <button
                type="button"
                className="op-bulkbar-btn"
                onClick={() => void baixarSelecionados()}
              >
                <Icon name="arrow-up-right" size={13} /> Baixar
              </button>
            </Guard>
            <Guard permission="autosped.dispatch">
              <button
                type="button"
                className="op-bulkbar-btn"
                disabled={reexec.isPending}
                onClick={reprocessarSelecionados}
              >
                <Icon name="activity" size={13} /> Reprocessar
              </button>
            </Guard>
            <button type="button" className="op-bulkbar-btn" onClick={() => setSelected(new Set())}>
              <Icon name="x" size={13} /> Limpar
            </button>
          </div>
        </div>
      )}

      <Table
        rows={files}
        columns={columns}
        getKey={(f) => String(f.id)}
        selection={{ selected, toggle, toggleAll, allSelected }}
        empty={
          <EmptyState icon="search" title={isLoading ? "Carregando…" : "Nenhum arquivo"} />
        }
        rowAction={(f) =>
          baixavel(f) ? (
            <Guard permission="autosped.download">
              <Button
                variant="ghost"
                size="sm"
                iconLeft="arrow-up-right"
                onClick={() => void baixar(f.id)}
              >
                Baixar
              </Button>
            </Guard>
          ) : f.status === "erro" ? (
            <Guard permission="autosped.dispatch">
              <Button
                variant="ghost"
                size="sm"
                iconLeft="activity"
                disabled={reexec.isPending}
                onClick={() => reexec.mutate({ dispatchId: f.dispatch_id })}
              >
                Reprocessar
              </Button>
            </Guard>
          ) : null
        }
      />

      {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}
    </div>
  );
}
